import { translate } from 'shared/lib/i18n.tsx';
import {
  createTargetInitialState,
  type TargetDisplayState,
  type TargetRow,
} from '../domain/TargetDisplayState.ts';
import type { IGameRenderer } from '../ports/IGameRenderer.ts';
import type { NativeGameFrame, NativeTargetPayload } from 'shared/lib/tv-game/nativeFrame.ts';
import { resolveTargetAttackPoseKeypoints } from 'shared/lib/tv-game/targetAttackFrame.ts';
import {
  computeRemoteViewLayout,
  computeSplitRemoteViewLayouts,
  type RemoteViewLayout,
} from 'shared/lib/tv-game/nativeLayout.ts';
import { isPointInsideRemoteCalibStandZone } from 'shared/lib/tv-game/remoteCalibStandZone.ts';
import type { PoseKeypoint } from 'shared/lib/tv-game/pose.ts';
import { wristsAsHands, WRIST_MIN_CONFIDENCE } from 'shared/lib/tv-game/pose.ts';
import type { BladeKey } from '../domain/GameState.ts';
import { applyNormFlip } from 'shared/lib/tv-game/normCoord.ts';
import type { CalibNormMap } from '../domain/TargetDisplayState.ts';
import { keypointsToCalibMapWithRefs, rawToGameNormForTargetAttack } from './targetAttackPoseNorm.ts';
import { OneEuroPoint } from 'shared/lib/tv-game/oneEuro.ts';
import { setCalibrationAlert } from '../games/shared/state/calibrationAlertStore.ts';

type TargetAttackSoundApi = {
  startBackgroundMusic: () => void;
  stopBackgroundMusic: () => void;
  playTargetTouch: (type: NativeTargetPayload['type']) => void;
};

const TRAIL_MAX_LENGTH = 20;
const USE_SKELETON_KEYPOINT_SMOOTHING = true;
const USE_SKELETON_TRAIL_SMOOTHING = true;

function fillColorForColorType(ct: NativeTargetPayload['colorType']): string {
  switch (ct) {
    case 'BOMB':
      return '#ef4444';
    case 'COMBO':
      return '#eab308';
    case 'PLAYER2':
      return '#a855f7';
    case 'ARCADE_NEXT':
      return '#eab308';
    case 'NORMAL':
    default:
      return '#22c55e';
  }
}

/**
 * В `labels` натив часто шлёт только «Lv 1» / «Lv1». На TV показываем «Уровень N · NN%» из `levelProgress`
 * (рисуется тем же `fillText` на canvas в `drawTargetLabels` — отдельный HTML не нужен).
 * Если уже было «Уровень 1 · 50%», суффикс снимаем и пересчитываем — чтобы обновлялся % без нового массива labels.
 */
function augmentNativeLevelLabel(
  text: string,
  levelProgress: number | null,
  gameMode: TargetDisplayState['gameMode'],
): string {
  const plain = text.trim().replace(/\s*·\s*\d+%\s*$/, '').trim();
  const m = plain.match(/^(?:Lv|Уровень)\s*(\d+)\s*$/i);
  if (!m) return text;
  if (gameMode === 'ARCADE') {
    return `${translate('game.level')} ${m[1]}`;
  }
  const pct =
    levelProgress !== null
      ? Math.round(Math.max(0, Math.min(1, levelProgress)) * 100)
      : 0;
  return `${translate('game.level')} ${m[1]} · ${pct}%`;
}

/**
 * target-attack: WebRTC JSON (targets + wrists). Pose axes are **0…1 = game rect**; values
 * outside are off-screen (no bbox stretch to fill the canvas). Same calibration flow as fruit-ninja-without-score.
 */
export class TargetAttackDisplayEngine {
  private readonly state: TargetDisplayState = createTargetInitialState();
  private canvasWidth = 1920;
  private canvasHeight = 1080;
  private cachedAspectRatio: number | undefined = undefined;
  private cachedFruitSize: number | undefined = undefined;
  private cachedNormRefWidth: number | undefined = undefined;
  private cachedNormRefHeight: number | undefined = undefined;
  private cachedFlipX: boolean | undefined = undefined;
  private cachedFlipY: boolean | undefined = undefined;
  private onHudCallback?: (score: number, lives: number, combo: number) => void;
  private playerCount = 1;
  private readonly playerOrder: string[] = [];
  private prevFrameIsRestart = false;
  private calibrationDispatched = false;
  private prevHitTargetIds = new Set<string>();
  private nextHitTargetIdsBuf = new Set<string>();
  private sound?: TargetAttackSoundApi;
  private readonly keypointFilters = new Map<string, OneEuroPoint>();
  private readonly trailFilters = new Map<BladeKey, OneEuroPoint>();

  onCalibrationComplete?: () => void;

  constructor(private readonly renderer: IGameRenderer) {}

  setSoundEngine(sound: TargetAttackSoundApi): void {
    this.sound = sound;
  }

  setPlayerCount(n: number): void {
    this.playerCount = n === 2 ? 2 : 1;
    this.state.splitTwoPlayers = this.playerCount === 2;
  }

  setCanvasSize(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
  }

  onHudUpdate(cb: (score: number, lives: number, combo: number) => void): void {
    this.onHudCallback = cb;
  }

  getPhase(): TargetDisplayState['phase'] {
    return this.state.phase;
  }

  /** Снимок DOM-HUD (score, пауза, уровень, progress). */
  getState(): Readonly<TargetDisplayState> {
    return this.state;
  }

  setPhase(phase: TargetDisplayState['phase']): void {
    this.state.phase = phase;
    if (phase !== 'playing') {
      this.sound?.stopBackgroundMusic();
    }
  }

  startGame(): void {
    console.log('[TargetAttack] startGame called');
    this.state.phase = 'playing';
    this.state.bladeTrails.clear();
    this.state.remoteView = null;
    this.state.leftView = null;
    this.state.rightView = null;
    this.cachedAspectRatio = undefined;
    this.cachedFruitSize = undefined;
    this.cachedNormRefWidth = undefined;
    this.cachedNormRefHeight = undefined;
    this.cachedFlipX = undefined;
    this.cachedFlipY = undefined;
    this.state.gameStartTime = Date.now();
    this.state.targets = [];
    this.state.labels = [];
    this.calibrationDispatched = false;
    this.prevHitTargetIds.clear();
    console.log('[TargetAttack] startGame: sound =', !!this.sound, ', calling startBackgroundMusic');
    this.sound?.startBackgroundMusic();
    this.onHudCallback?.(0, 3, 0);
    setCalibrationAlert(null);
  }

  stopGame(): void {
    this.state.phase = 'lobby';
    this.sound?.stopBackgroundMusic();
    this.prevHitTargetIds.clear();
    setCalibrationAlert(null);
  }

  startCalibration(): void {
    this.state.phase = 'calibration';
    this.state.bladeTrails.clear();
    this.state.calibNormPoints = new Map();
    this.state.calibNormPointsP2 = new Map();
    this.state.calibSliced = false;
    this.state.calibSlicedP2 = false;
    this.cachedAspectRatio = undefined;
    this.cachedFruitSize = undefined;
    this.cachedNormRefWidth = undefined;
    this.cachedNormRefHeight = undefined;
    this.cachedFlipX = undefined;
    this.cachedFlipY = undefined;
    this.state.gameStartTime = Date.now();
    this.playerOrder.length = 0;
    this.calibrationDispatched = false;
    this.sound?.stopBackgroundMusic();
    this.prevHitTargetIds.clear();
  }

  /** HUD/meta с нативки — до фильтра по фазе, чтобы WebRTC обновлял levelProgress даже до playing/calibration. */
  private applyHudFieldsFromFrame(frame: NativeGameFrame): void {
    if (frame.detectionProgress !== undefined) {
      this.state.detectionProgress = frame.detectionProgress;
    }
    if ('calibration' in frame) {
      setCalibrationAlert(this.state.phase === 'calibration' ? frame.calibration ?? null : null);
    }
    if (frame.gameMode !== undefined) this.state.gameMode = frame.gameMode;
    if (frame.score !== undefined) this.state.score = frame.score;
    if (frame.player1?.score !== undefined && frame.player1.score !== null) {
      this.state.player1Score = frame.player1.score;
    }
    if (frame.player2?.score !== undefined && frame.player2?.score !== null) {
      this.state.player2Score = frame.player2.score;
    }
    if (frame.streak !== undefined) this.state.streak = frame.streak;
    if ('lives' in frame) this.state.lives = frame.lives ?? null;
    if ('timeLeft' in frame) this.state.timeLeft = frame.timeLeft ?? null;
    if ('combo' in frame) this.state.combo = frame.combo ?? null;
    if (frame.isPaused !== undefined) {
      const wasPaused = this.state.isPaused;
      this.state.isPaused = frame.isPaused;
      if (!wasPaused && frame.isPaused) this.sound?.stopBackgroundMusic();
      if (wasPaused && !frame.isPaused && this.state.phase === 'playing' && !this.state.isGameOver) {
        this.sound?.startBackgroundMusic();
      }
    }
    if (frame.isGameOver !== undefined) {
      const wasGameOver = this.state.isGameOver;
      this.state.isGameOver = frame.isGameOver;
      if (!wasGameOver && frame.isGameOver) this.sound?.stopBackgroundMusic();
    }
    if (frame.currentLevel !== undefined) this.state.currentLevel = frame.currentLevel;
    if (frame.levelProgress !== undefined) this.state.levelProgress = frame.levelProgress;
  }

  onGameFrame(playerId: string, frame: NativeGameFrame): void {
    const restart = frame.isRestart === true;
    if (restart && !this.prevFrameIsRestart) {
      this.startCalibration();
      this.state.isGameOver = false;
      this.state.isPaused = false;
    }
    this.prevFrameIsRestart = restart;

    this.applyHudFieldsFromFrame(frame);

    /** Поза/мишени только в активных фазах; HUD (score, levelProgress, …) уже применён выше для WebRTC. */
    if (this.state.phase === 'lobby' || this.state.phase === 'countdown') return;

    const W = this.canvasWidth;
    const H = this.canvasHeight;
    if (frame.normRefWidth !== undefined) this.cachedNormRefWidth = frame.normRefWidth;
    if (frame.normRefHeight !== undefined) this.cachedNormRefHeight = frame.normRefHeight;
    if (frame.flipX !== undefined) this.cachedFlipX = frame.flipX;
    if (frame.flipY !== undefined) this.cachedFlipY = frame.flipY;
    if (frame.aspectRatio !== undefined) this.cachedAspectRatio = frame.aspectRatio;
    if (frame.fruitSize !== undefined) this.cachedFruitSize = frame.fruitSize;

    if (!this.playerOrder.includes(playerId)) {
      this.playerOrder.push(playerId);
    }

    const split = this.state.splitTwoPlayers;
    let layoutFull: RemoteViewLayout;
    let layoutLeft: RemoteViewLayout | null = null;
    let layoutRight: RemoteViewLayout | null = null;

    if (split) {
      const { left, right, full } = computeSplitRemoteViewLayouts(
        W,
        H,
        this.cachedFruitSize,
        this.cachedAspectRatio,
        { useNativeAspectRatio: true },
      );
      this.state.remoteView = full;
      this.state.leftView = left;
      this.state.rightView = right;
      layoutFull = full;
      layoutLeft = left;
      layoutRight = right;
    } else {
      layoutFull = computeRemoteViewLayout(W, H, this.cachedAspectRatio, this.cachedFruitSize, {
        useNativeAspectRatio: true,
      });
      this.state.remoteView = layoutFull;
      this.state.leftView = null;
      this.state.rightView = null;
    }

    const mergedPlayers =
      frame.player1 != null &&
      frame.player1.keypoints.length > 0 &&
      frame.player2 != null &&
      frame.player2.keypoints.length > 0;

    if (split && layoutLeft && layoutRight) {
      if (mergedPlayers) {
        this.state.calibNormPoints = this.buildCalibMapForSlot(frame.player1!.keypoints);
        this.state.calibNormPointsP2 = this.buildCalibMapForSlot(frame.player2!.keypoints);
        this.applyKeypointsToTrails('slot1', frame.player1!.keypoints, layoutFull);
        this.applyKeypointsToTrails('slot2', frame.player2!.keypoints, layoutFull);
        this.state.p1PlayerId = 'slot1';
        this.state.p2PlayerId = 'slot2';
      } else {
        const slot = this.playerOrder.indexOf(playerId);
        const kps = resolveTargetAttackPoseKeypoints(frame);
        if (slot === 0) {
          this.state.calibNormPoints = this.buildCalibMapForSlot(kps);
        } else if (slot === 1) {
          this.state.calibNormPointsP2 = this.buildCalibMapForSlot(kps);
        } else {
          this.state.calibNormPoints = this.buildCalibMapForSlot(kps);
        }
        this.applyKeypointsToTrails(playerId, kps, layoutFull);
        this.state.p1PlayerId = this.playerOrder[0] ?? null;
        this.state.p2PlayerId = this.playerOrder[1] ?? null;
      }
    } else {
      const kps = resolveTargetAttackPoseKeypoints(frame);
      this.state.calibNormPoints = this.buildCalibMapForSlot(kps);
      this.state.calibNormPointsP2 = new Map();
      this.applyKeypointsToTrails(playerId, kps, layoutFull);
      this.state.p1PlayerId = playerId;
      this.state.p2PlayerId = null;
    }

    if (this.state.phase === 'calibration') {
      if (!split || !layoutLeft || !layoutRight) {
        if (this.tryCalibHit(layoutFull, () => true)) {
          this.state.calibSliced = true;
        }
      } else {
        const leftFilter = (k: string): boolean =>
          k.startsWith('slot1_') ||
          (!!this.playerOrder[0] && k.startsWith(`${this.playerOrder[0]}_`));
        const rightFilter = (k: string): boolean =>
          k.startsWith('slot2_') ||
          (!!this.playerOrder[1] && k.startsWith(`${this.playerOrder[1]}_`));
        const leftCalibLayout = this.buildWiderSplitCalibLayout(layoutLeft, layoutFull);
        const rightCalibLayout = this.buildWiderSplitCalibLayout(layoutRight, layoutFull);

        if (!this.state.calibSliced && this.tryCalibHit(leftCalibLayout, leftFilter)) {
          this.state.calibSliced = true;
        }
        if (!this.state.calibSlicedP2 && this.tryCalibHit(rightCalibLayout, rightFilter)) {
          this.state.calibSlicedP2 = true;
        }
      }

      const ready = !split ? this.state.calibSliced : this.state.calibSliced && this.state.calibSlicedP2;
      if (ready && !this.calibrationDispatched) {
        this.calibrationDispatched = true;
        this.onCalibrationComplete?.();
      }
      return;
    }

    /** Gameplay often sends `FruitNinjaData` (keypoints + fruits) without `targets` — keep drawing pose; only refresh circles when native sends TA fields. */
    if (frame.targets !== undefined) {
      this.playTargetHitSounds(frame.targets);
      const gm = this.state.gameMode;
      this.state.targets = frame.targets
        .map((t) => this.targetToRow(t, gm))
        .filter((row): row is TargetRow => row !== null);
    }
    if (frame.labels !== undefined) {
      this.state.labels = frame.labels.map((l) => {
        const { nx, ny } = this.fruitNormToCanvas(l.x, l.y);
        return {
          nx,
          ny,
          text: augmentNativeLevelLabel(l.text, this.state.levelProgress, this.state.gameMode),
          color: l.color,
          alpha: l.alpha,
        };
      });
    } else if (frame.levelProgress !== undefined && this.state.labels.length > 0) {
      this.state.labels = this.state.labels.map((row) => ({
        ...row,
        text: augmentNativeLevelLabel(row.text, this.state.levelProgress, this.state.gameMode),
      }));
    }
  }

  private playTargetHitSounds(targets: readonly NativeTargetPayload[]): void {
    if (!this.sound) return;
    const nextHitIds = this.nextHitTargetIdsBuf;
    nextHitIds.clear();
    for (const target of targets) {
      if (!target.isHit) continue;
      nextHitIds.add(target.id);
      if (!this.prevHitTargetIds.has(target.id)) {
        this.sound.playTargetTouch(target.type);
      }
    }
    const tmp = this.prevHitTargetIds;
    this.prevHitTargetIds = nextHitIds;
    this.nextHitTargetIdsBuf = tmp;
    this.nextHitTargetIdsBuf.clear();
  }

  private tryCalibHit(layout: RemoteViewLayout, keyFilter: (k: string) => boolean): boolean {
    const now = Date.now();
    let hit = false;
    this.state.bladeTrails.forEach((trail, key) => {
      if (hit) return;
      if (!keyFilter(key)) return;
      // Find the most recent point within 250ms — scan from end, no alloc.
      let tip = null;
      for (let i = trail.length - 1; i >= 0; i--) {
        if (now - trail[i]!.t < 250) { tip = trail[i]!; break; }
      }
      if (!tip) return;
      if (isPointInsideRemoteCalibStandZone(tip.x, tip.y, layout, 'full')) {
        hit = true;
      }
    });
    return hit;
  }

  private buildWiderSplitCalibLayout(
    splitLayout: RemoteViewLayout,
    fullLayout: RemoteViewLayout,
  ): RemoteViewLayout {
    const desiredWidth = Math.min(fullLayout.gameWidth * 0.58, splitLayout.gameWidth * 1.7);
    const cx = splitLayout.offsetX + splitLayout.gameWidth / 2;
    const offsetX = cx - desiredWidth / 2;
    return {
      ...splitLayout,
      offsetX,
      gameWidth: desiredWidth,
    };
  }

  private targetToRow(
    t: NativeTargetPayload,
    gameMode: TargetDisplayState['gameMode'],
  ): TargetRow | null {
    if (t.x === 0 && t.y === 0 && !t.isHit) return null;
    const { nx, ny } = this.fruitNormToCanvas(t.x, t.y);
    // Native can omit `gameMode` in frame; rely on sequence numbers themselves for arcade-style ordering.
    const showArcade = gameMode === 'ARCADE' || (gameMode !== 'CLASSIC' && t.arcadeSequenceNumber > 0);
    return {
      nx,
      ny,
      radius: t.radius,
      fillColor: fillColorForColorType(t.colorType),
      lifetimeProgress: t.lifetimeProgress,
      hitAnimationProgress: t.hitAnimationProgress,
      arcadeSequenceNumber: t.arcadeSequenceNumber,
      showArcadeNumber: showArcade && t.type === 'NORMAL' && t.arcadeSequenceNumber > 0,
      isHit: t.isHit,
    };
  }

  private hasNormRefs(): boolean {
    const rw = this.cachedNormRefWidth;
    const rh = this.cachedNormRefHeight;
    return rw !== undefined && rh !== undefined && rw > 0 && rh > 0;
  }

  private getFlips(): { flipX: boolean; flipY: boolean } {
    return {
      flipX: this.cachedFlipX !== undefined ? this.cachedFlipX : this.readUrlNormFlipX(),
      flipY: this.cachedFlipY !== undefined ? this.cachedFlipY : this.readUrlNormFlipY(),
    };
  }

  /** Pose → game rect: 0…1 per axis; `normRef*` only for pixel buffer scaling (see {@link keypointsToCalibMapWithRefs}). */
  private buildCalibMapForSlot(kps: readonly PoseKeypoint[]): CalibNormMap {
    if (kps.length === 0) return new Map();
    const { flipX, flipY } = this.getFlips();
    const rw = this.cachedNormRefWidth;
    const rh = this.cachedNormRefHeight;
    return keypointsToCalibMapWithRefs(kps, rw, rh, flipX, flipY);
  }

  /**
   * Fruits / targets / labels: x/y are already normalized in JSON parsing (like `normalizeFruitEntry`) — only apply flip.
   * Do not pass through `toUnitScalar` + `normRef*` here (that path is for raw keypoint pixels).
   */
  private fruitNormToCanvas(fx: number, fy: number): { nx: number; ny: number } {
    const flipX = this.cachedFlipX !== undefined ? this.cachedFlipX : this.readUrlNormFlipX();
    const flipY = this.cachedFlipY !== undefined ? this.cachedFlipY : this.readUrlNormFlipY();
    return applyNormFlip(fx, fy, { flipX, flipY });
  }

  private readUrlNormFlipX(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('normFlipX') === '1';
    } catch {
      return false;
    }
  }

  private readUrlNormFlipY(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('normFlipY') === '1';
    } catch {
      return false;
    }
  }

  private applyKeypointsToTrails(
    playerId: string,
    keypoints: readonly PoseKeypoint[],
    layout: RemoteViewLayout,
  ): void {
    const { flipX, flipY } = this.getFlips();
    const rw = this.cachedNormRefWidth;
    const rh = this.cachedNormRefHeight;

    const hands = wristsAsHands([...keypoints]);
    const present = new Set<BladeKey>();
    const now = Date.now();
    const tSec = now / 1000;

    hands.forEach(({ hand, x, y, confidence }) => {
      const key: BladeKey = `${playerId}_${hand}`;

      if (confidence !== undefined && confidence < WRIST_MIN_CONFIDENCE) {
        return;
      }
      if (x === 0 && y === 0) {
        return;
      }

      present.add(key);

      if (!this.state.bladeTrails.has(key)) {
        this.state.bladeTrails.set(key, []);
      }
      const trail = this.state.bladeTrails.get(key)!;
      const raw = rawToGameNormForTargetAttack(x, y, rw, rh, flipX, flipY);
      let nx = raw.nx;
      let ny = raw.ny;

      if (USE_SKELETON_TRAIL_SMOOTHING) {
        let flt = this.trailFilters.get(key);
        if (!flt) {
          flt = new OneEuroPoint(4.0, 0.08);
          this.trailFilters.set(key, flt);
        }
        const filtered = flt.filter(raw.nx, raw.ny, tSec);
        nx = filtered.x;
        ny = filtered.y;
      }

      const px = layout.offsetX + nx * layout.gameWidth;
      const py = layout.offsetY + ny * layout.gameHeight;
      trail.push({ x: px, y: py, t: now });
      if (trail.length > TRAIL_MAX_LENGTH) trail.shift();
    });

    this.state.bladeTrails.forEach((trail, key) => {
      if (!key.startsWith(`${playerId}_`)) return;
      if (!present.has(key)) {
        trail.length = 0;
        this.trailFilters.get(key)?.reset();
      }
    });
  }

  tick(): void {
    this.renderer.renderTargetAttack(this.state);
  }
}
